/**
 * The contents of a scratchpad (editor, canvas).
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import classNames from "classnames";
import {compose} from "mu-lambda";
import * as React from "react";

import {IntegerField} from "@khanacademy/event-schemas";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    enums,
    publishAnalyticsEventForMinorConversion,
    withConversionContext,
    withMarkConversion,
    type PartialLearningTimeInfo,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {
    PageLifecycle,
    PageloadMarker,
    type TrackedPageSlug,
} from "@khan/analytics-performance";

import {moduleLayout} from "#modules-package/styles.ts";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {
    Scratchpad,
    UserScratchpad,
} from "#scratchpad-base-package/scratchpads.ts";
import type {ScratchpadChallenge} from "#scratchpad-challenge-package/scratchpad-challenge.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";

import CompletionCritter from "./completion-critter.tsx";
import {mapToGqlSpinoffOriginKind} from "./queries/gql-type-mappers.ts";
import ScratchpadSaveSpinOffButtons from "./scratchpad-save-spinoff-buttons.tsx";
import ScratchpadTalkthroughPlaybackBar from "./scratchpad-talkthrough-playback-bar.tsx";
import ScratchpadToolbar from "./scratchpad-toolbar.tsx";
import type {SaveProgramFn} from "./scratchpad-ui.tsx";
import type {LiveEditor} from "./types.ts";

type UnicodeLanguage = {
    ranges: RegExp;
    className: string;
};

// Unicode ranges taken from: http://kourge.net/projects/regexp-unicode-block
const EDITOR_FONT_OVERRIDES: Record<string, UnicodeLanguage> = {
    Arabic: {
        ranges: /[\u0600-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/,
        className: "scratchpad-editor-freemono-font-override",
    },
    Georgian: {
        ranges: /[\u10A0-\u10FF\u2D00-\u2D2F]/,
        className: "scratchpad-editor-freemono-font-override",
    },
    Hebrew: {
        ranges: /[\u0590-\u05FF]/,
        className: "scratchpad-editor-freemono-font-override",
    },
};

const getScratchpadTrackedPageSlug = (
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad,
): TrackedPageSlug => {
    let pageName: TrackedPageSlug = "coding_program_page";

    if (scratchpad.isProjectOrFork()) {
        pageName = "coding_project_page";
    } else if (scratchpad.isChallenge()) {
        pageName = "coding_challenge_page";
    } else if (scratchpad.isTalkie()) {
        pageName = "coding_talkthrough_page";
    } else if (scratchpad.isOfficial()) {
        pageName = "coding_interactive_page";
    }

    return pageName;
};

type OwnProps = {
    // The current version of the app engine server
    appVersion: string;
    // The profile object of the person who made this program
    creatorProfile?: ProfileModelType;
    // Is this scratchpad embedded in some other context
    embedded: boolean;
    // The challenge we are currently facing
    // @ts-expect-error - TS2749 - 'ScratchpadChallenge' refers to a value, but is being used as a type here. Did you mean 'typeof ScratchpadChallenge'?
    challenge: ScratchpadChallenge | null | undefined;
    // Is this scratchpad embedded using an iframe
    embeddedAndWithFrame?: boolean;
    // Determine if the save button should be hidden
    hideSave: boolean;
    // Does this scratchpad have any audio associated with it
    hasAudio: boolean;
    // Should the code editor be hidden?
    hideEditor: boolean;
    // Can this scratchpad be edited by the current user and are
    // we in an edit mode
    inEditModeAndCanEdit?: boolean;
    // Is this particular scratchpad a challenge
    isChallenge: boolean;
    // Is this scratchpad new (e.g. on the /new page)
    isNew?: boolean;
    // Is this a project?
    isProject: boolean;
    // A promise that will resolve when we have the ScratchpadRecord
    liveEditorPromise: Promise<LiveEditor>;
    // Fires during a seek
    onSeek: (timeInSeconds: number) => unknown;
    // When the seeking ends
    onSeekEnd: () => unknown;
    // When the seeking begins
    onSeekStart: () => unknown;
    // Handle when the "Start over" button is clicked
    onStartOver: () => unknown;
    // Handle when the "Request help" button is clicked
    onRequestHelp: () => unknown;
    // Handle when the "Undo" button is clicked
    onUndo: () => unknown;
    // The label to use for the restart button
    restartLabel: string;
    // When was the last time this program was updated (ISO Date)
    lastUpdatedTime: string;
    // The parent of the program which is being rendered (if exists)
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    originScratchpad: Scratchpad | null | undefined;
    // A set of properties to add to the Sandbox URL
    sandboxProps: string;
    // The URL in which all the code is being run
    sandboxUrl: string;
    // The scratchpad Backbone model which is being rendered
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
    // Should the project help button be shown
    showProjectHelpButton: boolean;
    // Should the start over button be shown
    showStartOverButton: boolean;
    // Should the project eval area be shown
    showProjectEvalArea: boolean;
    // Number of spin-offs (if this is a project)
    numProjectSpinoffs: number;
    // Topic used for getting the relativeUrl for linking
    topic: {
        relativeUrl: string;
    };
    // The UserScratchpad in use on the project or challenge
    // @ts-expect-error - TS2749 - 'UserScratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof UserScratchpad'?
    userScratchpad: UserScratchpad;
    // A URL for a Youtube replacement for the talkthrough
    youtubeUrl: string;
    // Is this scratchpad able to be saved using a normal "Save" button
    isSaveable: boolean;
    // Determine if a spin-off is currently allowed
    isSpinOffAllowed: () => boolean;
    // A place to register a callback for external triggering of
    // the save flow on a scratchpad
    registerTriggerSave: (
        callback: (saveSettings: boolean) => unknown,
    ) => unknown;
    // A place to register a callback for external triggering of
    // the spin-off flow on a scratchpad
    registerTriggerSpinOff: (callback: () => unknown) => unknown;
    // Handle saving the current Program (previously known as a Scratchpad)
    saveProgram: SaveProgramFn;
    // Handle setting the title on the scratchpad
    setTitle: (title: string) => Promise<unknown>;
    // A way to register a callback that will fire when the code runs
    registerCodeRun: (callback: () => unknown) => unknown;
    // A way to register a callback that will fire when the code changes
    registerCodeChange: (callback: () => unknown) => unknown;
    // A way to register a callback that will fire when user changes the code
    registerUserCodeChange: (callback: () => unknown) => unknown;
    // A function that determines whether the current code should
    // cause the restart button to be hidden
    shouldHideRestart: () => boolean;
};

type Props = OwnProps & WithMarkConversionProps;

class ScratchpadContent extends React.Component<Props> {
    // @ts-expect-error - TS2564 - Property '_codeHasRun' has no initializer and is not definitely assigned in the constructor.
    _codeHasRun: boolean;

    componentDidMount() {
        this.props.registerCodeRun(this.handleCodeRun);
        this.props.registerUserCodeChange(this.markConversion);
    }

    handleCodeRun: () => void = () => {
        if (this._codeHasRun) {
            return;
        }

        this._codeHasRun = true;

        const {scratchpad} = this.props;

        // We track both sufficiently usable and fully interactive at the same
        // time for scratchpad pages. It's possible that sufficiently usable
        // may be earlier for some pages (such as projects) but this may be
        // fine for now. A <PageloadMarker> is set below but it doesn't really
        // do anything since all the timing is logged here instead.
        const pageName = getScratchpadTrackedPageSlug(scratchpad);
        PageLifecycle.reportPageUsableTiming(pageName);
        PageLifecycle.reportPageInteractiveTiming(pageName);

        const contentKind = scratchpad.get("contentKind");
        const scratchpadId = scratchpad.get("id");

        if (contentKind != null && scratchpadId != null) {
            const analyticsContentKind = mapToGqlSpinoffOriginKind(contentKind);

            if (analyticsContentKind === "PROGRAM") {
                const programId = parseInt(scratchpadId, 10);
                if (!isNaN(programId)) {
                    publishAnalyticsEventForMinorConversion({
                        eventType: "ComputingUserProgramLoaded",
                        eventSchemaVersion: 2,
                        programId: new IntegerField(programId),
                    });
                }
            } else {
                publishAnalyticsEventForMinorConversion({
                    eventType: "ComputingLibraryProgramLoaded",
                    eventSchemaVersion: 2,
                    contentId: scratchpadId,
                    contentKind: analyticsContentKind,
                });
            }
        }
    };

    _sawCodeChange = false;

    markConversion: () => void = () => {
        // Only mark the conversion once.
        // Continuation conversions will be fired continuously as long
        // as the user continues to actively use the browser window
        if (this._sawCodeChange) {
            return;
        }

        this._sawCodeChange = true;

        const {markConversion, scratchpad} = this.props;
        const contentKind = scratchpad.get("contentKind");
        const scratchpadId = scratchpad.get("id");

        if (contentKind != null && scratchpadId != null) {
            const analyticsContentKind = mapToGqlSpinoffOriginKind(contentKind);

            if (analyticsContentKind === "PROGRAM") {
                const programId = parseInt(scratchpadId, 10);
                if (!isNaN(programId)) {
                    publishAnalyticsEventForMinorConversion({
                        eventType: "ComputingUserProgramCodeEdited",
                        eventSchemaVersion: 2,
                        programId: new IntegerField(programId),
                    });
                }
            } else {
                publishAnalyticsEventForMinorConversion({
                    eventType: "ComputingLibraryProgramCodeEdited",
                    eventSchemaVersion: 2,
                    contentId: scratchpadId,
                    contentKind: analyticsContentKind,
                });
            }
        }

        // We don't track edits for talk-throughs with BigBingo conversions,
        // as it's simpler to consider all time spent on a talkthrough page as
        // WATCHING.
        if (contentKind !== "Talkthrough") {
            markConversion({id: "code_edited"});
        }
    };

    isFullyWatched(): boolean {
        return this.props.userScratchpad.get("fullyWatched");
    }

    /**
     * Returns a class name to override Ace editor fixed-width font, or null if
     * no override is required.
     *
     * The returned class should override with font family that has support for
     * the extended unicode range detected in the program code.
     *
     * Ace editor is not able to correctly calculate cursor column position in
     * row if not all character glyphs are rendered from the same monospace
     * font. It assumes all characters have the same fixed width. However, this
     * may not be the case if the selected font is missing glyphs, the browser
     * will fallback to using another font, possibly resulting in mixed width
     * characters being rendered in the editor.
     */
    maybeCustomEditorFontClass(): string | null | undefined {
        const {hideEditor, scratchpad} = this.props;

        if (hideEditor) {
            return null;
        }

        // We assume that a user who is not the owner of the program probably
        // will not interact with the program code, except if it is a challenge.
        // We err on the side of not switching fonts if we don't expect
        // interaction, to minimize the visual disruption, and to continue using
        // the preferred editor font.
        if (!scratchpad.isOwner() && !scratchpad.isChallenge()) {
            return null;
        }

        const code = scratchpad.getCode();

        if (!code) {
            return null;
        }

        // Return override class name if code contains a unicode character in
        // the extended languages list. We further assume that code will not
        // contain a mix of languages, returning on the first match.
        for (const {ranges, className} of Object.values(
            EDITOR_FONT_OVERRIDES,
        )) {
            if (code.match(ranges)) {
                return className;
            }
        }

        return null;
    }

    renderProjectEvalArea(): React.ReactNode {
        if (!this.props.showProjectEvalArea) {
            return null;
        }

        return (
            <div
                className={classNames(
                    "scratchpad-project-eval-mount",
                    css(styles.evalMount),
                )}
            />
        );
    }

    renderYoutubePlaybackLink(): React.ReactNode {
        if (!this.props.youtubeUrl) {
            return null;
        }

        return (
            <div
                className="scratchpad-editor-youtube-link alert alert-info"
                style={{display: "none"}}
            >
                <span className="close" data-dismiss="alert">
                    &#215;
                </span>
                <a href={this.props.youtubeUrl} target="_blank">
                    {t`If the audio never loads, reload the page or watch on YouTube.`}
                </a>
            </div>
        );
    }

    renderAudioPlayback(): React.ReactNode {
        if (!this.props.hasAudio) {
            return null;
        }

        return (
            <div>
                <div className="scratchpad-editor-bigplay-loading">
                    <DeprecatedSpinner />
                    <span className="hide-text">{t`Loading...`}</span>
                </div>
                {/* This cannot be removed, if we want Flash to keep working! */}
                <div id="sm2-container">
                    {t`Enable Flash to load audio:`}
                    <br />
                </div>
                {this.renderYoutubePlaybackLink()}
                <button
                    className="scratchpad-editor-bigplay-button"
                    style={{display: "none"}}
                >
                    <DeprecatedIcon icon={icons.play} />
                    <span className="hide-text">{t`Play`}</span>
                </button>
            </div>
        );
    }

    renderPlaybackBar(): React.ReactNode {
        if (!this.props.hasAudio) {
            return null;
        }

        const {liveEditorPromise, onSeek, onSeekEnd, onSeekStart, youtubeUrl} =
            this.props;

        const {
            isSaveable,
            isSpinOffAllowed,
            registerTriggerSave,
            registerTriggerSpinOff,
            saveProgram,
            scratchpad,
            setTitle,
        } = this.props;

        const {challenge, userScratchpad} = this.props;

        return (
            <div className={css(styles.playbar)}>
                <div className={css(styles.playbackBar)}>
                    <ScratchpadTalkthroughPlaybackBar
                        liveEditorPromise={liveEditorPromise}
                        onSeek={onSeek}
                        onSeekEnd={onSeekEnd}
                        onSeekStart={onSeekStart}
                        youtubeUrl={youtubeUrl}
                    />
                </div>
                <ScratchpadSaveSpinOffButtons
                    isSaveable={isSaveable}
                    isSpinOffAllowed={isSpinOffAllowed}
                    registerTriggerSave={registerTriggerSave}
                    registerTriggerSpinOff={registerTriggerSpinOff}
                    saveProgram={saveProgram}
                    scratchpad={scratchpad}
                    setTitle={setTitle}
                />
                {this.isFullyWatched() && (
                    <CompletionCritter
                        challenge={challenge}
                        userScratchpad={userScratchpad}
                        showAbove={true}
                    />
                )}
            </div>
        );
    }

    renderSaveProgress(): React.ReactNode {
        if (this.props.embeddedAndWithFrame) {
            return null;
        }

        return <div id="save-progress-text" />;
    }

    renderEditor(): React.ReactNode {
        return (
            <div
                className={classNames(
                    "scratchpad-editor-wrap",
                    "overlay-container",
                    css(
                        styles.editorWrap,
                        this.props.hideEditor && styles.editorHidden,
                    ),
                )}
            >
                <div
                    className={classNames(
                        "scratchpad-editor-tabs",
                        css(styles.editorContainer, styles.noBorder),
                    )}
                >
                    <div
                        id="scratchpad-code-editor-tab"
                        className={classNames(
                            "scratchpad-editor-tab",
                            // This is applied on an ancestor of .ace_editor,
                            // as setting class directly on the element breaks
                            // the editor when class is changed dynamically.
                            this.maybeCustomEditorFontClass(),
                            css(styles.editorTab),
                        )}
                    >
                        <div
                            className={classNames(
                                "scratchpad-editor",
                                "scratchpad-ace-editor",
                                css(styles.inputFrame, styles.noBorder),
                            )}
                        />
                        <div
                            className="overlay disable-overlay"
                            style={{display: "none"}}
                        />
                        {this.renderAudioPlayback()}
                    </div>
                </div>
            </div>
        );
    }

    renderDragHandle(): React.ReactNode {
        if (!this.props.scratchpad.isResizableType()) {
            return null;
        }

        return (
            <div
                className={classNames(
                    "scratchpad-drag-handle",
                    css(
                        styles.dragHandle,
                        this.props.hideEditor && styles.editorHidden,
                    ),
                )}
            />
        );
    }

    renderOutputFrame(): React.ReactNode {
        const sandboxUrl = this.props.sandboxUrl;
        const isResizable = this.props.scratchpad.isResizableType();
        const hideEditor = this.props.hideEditor;

        if (!sandboxUrl) {
            return null;
        }

        {
            /* Extra data-src attribute to work around cross-origin access
        policies. */
        }
        return (
            // eslint-disable-next-line jsx-a11y/iframe-has-title -- TODO: Address a11y error
            <iframe
                id="output-frame"
                className={css(
                    styles.noBorder,
                    styles.outputFrame,
                    isResizable &&
                        hideEditor &&
                        styles.outputFrameNoEditorResizable,
                )}
                sandbox={this.props.sandboxProps}
                src={`${sandboxUrl}&v=${
                    this.props.appVersion
                    // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
                    // eslint-disable-next-line no-restricted-syntax
                }&host=${encodeURIComponent(window.location.host)}`}
                data-src={sandboxUrl}
                style={{width: "100%"}}
            />
        );
    }

    renderCanvasToolbar(): React.ReactNode {
        if (this.props.hideEditor && this.props.hasAudio) {
            return this.renderAudioPlayback();
        }

        return null;
    }

    renderDrawCanvas(): React.ReactNode {
        const width = this.props.scratchpad.get("width");
        const height = this.props.scratchpad.get("height");

        return (
            <canvas
                className="scratchpad-draw-canvas"
                style={{display: "none"}}
                width={width}
                height={height}
            />
        );
    }

    // Canvases (Drawing + Output)
    renderCanvas(): React.ReactNode {
        if (!this.props.sandboxUrl) {
            // NOTE(jeresig): LiveEditor depends on there being a draw canvas
            // even though we don't use it at all!
            return this.renderDrawCanvas();
        }

        const isResizable = this.props.scratchpad.isResizableType();
        const hideEditor = this.props.hideEditor;

        return (
            <div
                className={classNames(
                    "scratchpad-canvas-wrap",
                    css(
                        this.props.hideEditor && styles.canvasWrapNoEditor,
                        styles.canvasWrap,
                    ),
                )}
            >
                <div
                    id="output"
                    className={css(
                        styles.output,
                        isResizable &&
                            hideEditor &&
                            styles.outputNoEditorResizable,
                    )}
                >
                    {this.renderOutputFrame()}
                    {this.renderDrawCanvas()}

                    <div
                        className="overlay disable-overlay"
                        style={{display: "none"}}
                    />

                    <div className="scratchpad-canvas-loading">
                        <DeprecatedSpinner />
                        <span className="hide-text">{t`Loading...`}</span>
                    </div>
                </div>
                {this.renderCanvasToolbar()}
            </div>
        );
    }

    renderDescription(): React.ReactNode {
        // We render descriptions in the About tab for Talkthroughs & Challenges
        // sync-start:program-description-render 2022426783 ./apps/khanacademy/src/scratchpads-package/scratchpad-about.tsx
        if (
            this.props.scratchpad.get("contentKind") === "Talkthrough" ||
            this.props.scratchpad.get("contentKind") === "Challenge"
        ) {
            return null;
        }
        // sync-end:program-description-render

        let description = this.props.scratchpad.get("translatedDescription");

        if (!description) {
            const originScratchpad = this.props.originScratchpad;
            if (originScratchpad && originScratchpad.isProject()) {
                description = originScratchpad.get("translatedDescription");
            }
        }

        if (!description) {
            return null;
        }

        return (
            <div
                className={css(styles.description)}
                dangerouslySetInnerHTML={{
                    __html: description,
                }}
            />
        );
    }

    render(): React.ReactNode {
        const isResizable = this.props.scratchpad.isResizableType();
        const hideEditor = this.props.hideEditor;

        const {
            challenge,
            embeddedAndWithFrame,
            hideSave,
            inEditModeAndCanEdit,
            isChallenge,
            isNew,
            isProject,
            onStartOver,
            onRequestHelp,
            onUndo,
            restartLabel,
            scratchpad,
            showProjectHelpButton,
            showStartOverButton,
            topic,
            userScratchpad,
            isSaveable,
            isSpinOffAllowed,
            registerTriggerSave,
            registerTriggerSpinOff,
            saveProgram,
            setTitle,
            liveEditorPromise,
            registerCodeChange,
            shouldHideRestart,
        } = this.props;

        return (
            <div className={css(styles.wrap)}>
                {this.renderDescription()}
                {this.renderProjectEvalArea()}
                <div
                    className={classNames(
                        "scratchpad-wrap",
                        this.props.sandboxUrl ? "" : "no-output",
                        css(
                            styles.wrapScratchpad,
                            hideEditor &&
                                isResizable &&
                                styles.wrapScratchpadNoEditorResizable,
                        ),
                    )}
                >
                    <div className={css(styles.wrapScratchpadOuter)}>
                        <div
                            className={css(
                                styles.wrapScratchpadInner,
                                !this.props.hideEditor &&
                                    styles.wrapScratchpadInnerBorder,
                            )}
                        >
                            {this.renderEditor()}
                            {this.renderDragHandle()}
                            {this.renderCanvas()}
                        </div>
                        {this.renderPlaybackBar()}
                        {!this.props.hasAudio && !this.props.hideEditor && (
                            <ScratchpadToolbar
                                challenge={challenge}
                                embeddedAndWithFrame={embeddedAndWithFrame}
                                hideSave={hideSave}
                                inEditModeAndCanEdit={inEditModeAndCanEdit}
                                isChallenge={isChallenge}
                                isNew={isNew}
                                isProject={isProject}
                                onStartOver={onStartOver}
                                onRequestHelp={onRequestHelp}
                                onUndo={onUndo}
                                restartLabel={restartLabel}
                                scratchpad={scratchpad}
                                showProjectHelpButton={showProjectHelpButton}
                                showStartOverButton={showStartOverButton}
                                topic={topic}
                                userScratchpad={userScratchpad}
                                isSaveable={isSaveable}
                                isSpinOffAllowed={isSpinOffAllowed}
                                registerTriggerSave={registerTriggerSave}
                                registerTriggerSpinOff={registerTriggerSpinOff}
                                saveProgram={saveProgram}
                                setTitle={setTitle}
                                liveEditorPromise={liveEditorPromise}
                                registerCodeChange={registerCodeChange}
                                shouldHideRestart={shouldHideRestart}
                            />
                        )}
                    </div>
                </div>
                {this.renderSaveProgress()}
                {/**
                 * Page load is tracked in handleCodeRun by tracking when the
                 * code actually executes. This indicates that the code is
                 * fully interactive. Sufficiently usable may (arguably) be
                 * earlier but we keep it the same as fully interactive for now.
                 */}
                <PageloadMarker
                    pageName={getScratchpadTrackedPageSlug(scratchpad)}
                    fullyInteractive={false}
                    sufficientlyUsable={false}
                />
            </div>
        );
    }
}

const icons = {
    play: `M1.6,9.9C1.5,10,1.3,10,1.2,10C1.1,10,1,10,0.9,9.9C0.7,9.8,0.6,9.6,
        0.6,9.4V0.6c0-0.2,0.2-0.4,0.4-0.5C1.1,0,1.4,0,1.6,0.1l7.6,4.5c0.2,0.1,
        0.3,0.3,0.3,0.5c0,0.2-0.1,0.4-0.3,0.5L1.6,9.9z`,
} as const;

const scratchpadBorder = `1px solid ${color.offBlack16}`;
const scratchpadDim = 400;

const styles = StyleSheet.create({
    canvasWrap: {
        flexShrink: 0,
    },
    description: {
        marginBottom: 15,
    },
    dragHandle: {
        background: color.offBlack16,
        cursor: "ew-resize",
        display: "table-cell",
        flexShrink: 0,
        width: 2,
    },
    editorWrap: {
        borderRight: scratchpadBorder,
        display: "flex",
        flexDirection: "column",
        // Override the ridiculous margin-right: 412px
        marginRight: "auto",
        minHeight: scratchpadDim,
        flexGrow: 1,
        flexShrink: 1,
    },
    editorHidden: {
        display: "none",
    },
    editorContainer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        flexShrink: 1,
        position: "relative",
    },
    // This is ambiguously named -- refers to the contents of a tab, *NOT*
    // the tab button or buttons at the top of the challenge content editor.
    editorTab: {
        position: "absolute",
        padding: 0,
        height: "100%",
        width: "100%",
    },
    evalMount: {
        marginTop: -10,
    },
    noBorder: {
        border: "none",
    },
    inputFrame: {
        height: "100%",
        position: "relative",
    },
    canvasWrapNoEditor: {
        margin: "0 auto",
    },
    output: {
        border: "none",
        margin: "auto",
        minHeight: scratchpadDim,
        minWidth: scratchpadDim,
    },
    outputNoEditorResizable: {
        minWidth: "100%",
    },
    outputFrame: {
        // NOTE(jeresig): LiveEditor assumes that the canvas output will
        // still have a 1px border around it, so we need to fake it to
        // ensure that the height calculations are correct.
        borderBottom: "1px transparent solid",
        borderTop: "1px transparent solid",
        marginTop: -1,
        minWidth: scratchpadDim,
    },
    outputFrameNoEditorResizable: {
        minWidth: "100%",
        left: 0,
    },
    playbar: {
        border: scratchpadBorder,
        boxSizing: "border-box",
        display: "flex",
        height: 64,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 12,
    },
    playbackBar: {
        flex: "1 0 0",
    },
    wrap: {
        ...moduleLayout.defaultAlignment,
        display: "flex",
        flexDirection: "column",
    },
    wrapScratchpad: {
        // These two make .wrapInner hide border-radius overflow *shrug*
        position: "relative",
        zIndex: 0,
        flexGrow: 1,
        display: "flex",
        flexDirection: "row",
        minWidth: scratchpadDim,
    },
    wrapScratchpadNoEditorResizable: {
        minWidth: "100%",
    },
    wrapScratchpadOuter: {
        flexGrow: 1,
        overflow: "auto", // Ensures long SQL/Webpage output can be scrolled
    },
    wrapScratchpadInner: {
        display: "flex",
    },
    wrapScratchpadInnerBorder: {
        border: scratchpadBorder,
        borderBottom: "none", // The toolbar provides the bottom border
    },
});

const mapPropsToLearningTime = (
    ownProps: OwnProps,
): PartialLearningTimeInfo => {
    return {
        activity: enums.Activity.PROGRAMMING,
        product: enums.Product.OTHER_PRODUCT,
    };
};

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof ScratchpadContent,
        React.ComponentProps<typeof ScratchpadContent>
    >
>;

export default compose(
    withBackboneModels((props: Props) => {
        const models = [props.scratchpad];

        if (props.originScratchpad) {
            models.push(props.originScratchpad);
        }

        if (props.creatorProfile) {
            models.push(props.creatorProfile);
        }

        if (props.userScratchpad) {
            models.push(props.userScratchpad);
        }

        return models;
    }),
    withConversionContext(mapPropsToLearningTime),
    withMarkConversion,
)(ScratchpadContent) as React.ComponentType<ExportProps>;
